import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import type { Hotel } from "@/types/hotel";

interface TenantAccessGroupFieldsProps {
  usersSearch: string;
  hotelSearch: string;
  hotels: Hotel[];
  selectedHotelIds: string[];
  isHotelsLoading: boolean;
  onUsersSearchChange: (value: string) => void;
  onHotelSearchChange: (value: string) => void;
  onToggleHotel: (hotelId: string) => void;
  onToggleAllVisibleHotels: (hotelIds: string[], checked: boolean) => void;
  disabled?: boolean;
}

export const TenantAccessGroupFields: React.FC<
  TenantAccessGroupFieldsProps
> = ({
  usersSearch,
  hotelSearch,
  hotels,
  selectedHotelIds,
  isHotelsLoading,
  onUsersSearchChange,
  onHotelSearchChange,
  onToggleHotel,
  onToggleAllVisibleHotels,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const filteredHotels = useMemo(() => {
    const search = hotelSearch.trim().toLowerCase();
    if (!search) {
      return hotels;
    }

    return hotels.filter((hotel) => hotel.name.toLowerCase().includes(search));
  }, [hotelSearch, hotels]);

  const visibleHotelIds = useMemo(
    () => filteredHotels.map((hotel) => hotel.id),
    [filteredHotels],
  );
  const areAllVisibleHotelsSelected =
    visibleHotelIds.length > 0 &&
    visibleHotelIds.every((hotelId) => selectedHotelIds.includes(hotelId));

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          value={usersSearch}
          onChange={(event) => onUsersSearchChange(event.target.value)}
          icon={<Search className="text-muted-foreground h-4 w-4" />}
          placeholder={t("users.groups.create.searchPlaceholder", "Search")}
          label={t("users.groups.create.usersLabel", "Users")}
          disabled={disabled}
        />
        <Input
          value={hotelSearch}
          onChange={(event) => onHotelSearchChange(event.target.value)}
          icon={<Search className="text-muted-foreground h-4 w-4" />}
          placeholder={t("users.groups.create.searchPlaceholder", "Search")}
          label={t("users.groups.create.hotelLabel", "Hotel")}
          disabled={disabled}
        />
      </div>

      {isHotelsLoading || filteredHotels.length > 0 ? (
        <div className="border-border max-h-[180px] overflow-y-auto rounded-md border p-2">
          {isHotelsLoading ? (
            <p className="text-muted-foreground px-2 py-1 text-sm">
              {t("common.loading", "Loading...")}
            </p>
          ) : null}
          {!isHotelsLoading && filteredHotels.length > 0 ? (
            <div className="border-border hover:bg-muted flex items-center gap-2 border-b px-2 py-1.5">
              <Checkbox
                id="select-all-visible-hotels"
                checked={areAllVisibleHotelsSelected}
                onCheckedChange={(checked) =>
                  onToggleAllVisibleHotels(visibleHotelIds, checked === true)
                }
                disabled={disabled}
              />
              <label
                htmlFor="select-all-visible-hotels"
                className="text-foreground min-w-0 flex-1 cursor-pointer truncate text-sm font-medium"
              >
                {t("users.groups.create.selectAll", "Select all")}
              </label>
            </div>
          ) : null}
          {filteredHotels.map((hotel) => {
            const checkboxId = `hotel-${hotel.id}`;

            return (
              <div
                key={hotel.id}
                className="hover:bg-muted flex items-center gap-2 rounded-sm px-2 py-1.5"
              >
                <Checkbox
                  id={checkboxId}
                  checked={selectedHotelIds.includes(hotel.id)}
                  onCheckedChange={() => onToggleHotel(hotel.id)}
                  disabled={disabled}
                />
                <label
                  htmlFor={checkboxId}
                  className="text-foreground min-w-0 flex-1 cursor-pointer truncate text-sm"
                >
                  {hotel.name}
                </label>
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
};
